import {
  ChangeDetectionStrategy,
  Component,
  effect,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type InputType = 'text' | 'email' | 'password' | 'tel' | 'number';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  id = input.required<string>();
  label = input<string>('');
  placeholder = input<string>('');
  type = input<InputType>('text');
  value = input<string>('');
  error = input<string>('');
  disabled = input<boolean>(false);
  /** aria-label del input nativo, para casos sin `label` visible (p. ej. buscadores compactos). */
  ariaLabel = input<string>('');

  valueChange = output<string>();

  /** Estado interno que convive con CVA y con [value] del input signal. */
  protected readonly internalValue = signal<string>('');
  protected readonly hasFocus = signal(false);
  protected readonly cvaDisabled = signal(false);

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  // True en cuanto Angular nos engancha como ControlValueAccessor
  // (formControlName / formControl). A partir de ese momento el form
  // manda: ignoramos el sync desde el input() `value` para no pisar
  // valores escritos por el form (p.ej. autofill vía patchValue).
  private isCvaActive = false;

  constructor() {
    // Sincroniza el `input()` externo con el signal interno SÓLO mientras
    // no estemos bajo control de un reactive form. Si hay CVA activo, el
    // dueño del valor es el form y el padre no debería pasar [value].
    effect(() => {
      const externalValue = this.value();
      if (this.isCvaActive) return;
      this.internalValue.set(externalValue);
    });
  }

  // ── ControlValueAccessor ────────────────────────────────────────────
  writeValue(value: string | null | undefined): void {
    this.internalValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
    this.isCvaActive = true;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }

  // ── Disabled unificado (input() + CVA setDisabledState) ─────────────
  protected readonly isDisabled = () => this.disabled() || this.cvaDisabled();

  // ── Eventos nativos ─────────────────────────────────────────────────
  protected onInput(event: Event): void {
    const newValue = (event.target as HTMLInputElement).value;
    this.internalValue.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }

  protected onBlur(): void {
    this.hasFocus.set(false);
    this.onTouched();
  }
}
